// Basic
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Select from '@/common-utils/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import PortTypes from '../../../enums/PortTypes';
import getTranslatedText from '@/common-utils/functions/getTranslatedText';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.setPortFormData
 * @returns {JSX} FormAddPort
 */
function FormAddPort({ setPortFormData }) {

    // Constants
    const portTypesOptions = Object.values(PortTypes).map(({ name, module }) => ({
        name,
        value: module
    }));

    // Form
    const addPortFormSchema = z.object({
        type: z.enum(portTypesOptions.map(portType => portType.value)),
        value: z.string()
            .refine(
                value => /^\d{5}$/.test(value) && value?.length,
                { message: getTranslatedText('modal.add_port.form.value.error', nsOrganization) }
            )
    });
    const addPortForm = useForm({
        resolver: zodResolver(addPortFormSchema),
        mode: 'onChange',
        defaultValues: {
            type: portTypesOptions[0].value,
            value: ''
        }
    });

    // Hooks
    useEffect(() => {
        setPortFormData({ data: addPortForm.getValues(), isValid: addPortForm.formState.isValid });
    }, [addPortForm.watch(), addPortForm.formState]);

    return (
        <Form {...addPortForm}>
            <div className='space-y-2'>
                <FormField
                    name='type'
                    control={addPortForm.control}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel required>
                                <Entity namespace={nsOrganization} entity='modal.add_port.type' />
                            </FormLabel>
                            <FormControl>
                                <Select
                                    {...field}
                                    options={portTypesOptions}
                                    namespace={nsOrganization}
                                    prefixEntity={'modal.add_port.types.'}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    name='value'
                    control={addPortForm.control}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel required>
                                <Entity namespace={nsOrganization} entity='modal.add_port.value' />
                            </FormLabel>
                            <FormControl>
                                <Input
                                    {...field}
                                    type='text'
                                    maxLength='5'
                                    autoComplete='off'
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
        </Form>
    );
}

FormAddPort.propTypes = {
    setPortFormData: PropTypes.func.isRequired
};

export default FormAddPort;
